import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getEaApiKey,
  regenerateEaApiKey,
  getEaSettings,
  updateEaSettings,
  getWatchlist,
  addWatchlistEntry,
  removeWatchlistEntry,
  getEaSignals,
  getEaEvents,
} from "../services/eaApi";
import { RADAR_SYMBOLS } from "../services/radar/radarConfig";

const REFRESH_INTERVAL_MS = 10000;

// `timeframe` is stored/matched in SECONDS everywhere in this codebase
// (see App.js's default context.timeframe = 300, RadarDashboard's filters).
const TIMEFRAME_OPTIONS = [
  { label: "M1", seconds: 60 },
  { label: "M3", seconds: 180 },
  { label: "M5", seconds: 300 },
  { label: "M15", seconds: 900 },
  { label: "M30", seconds: 1800 },
  { label: "H1", seconds: 3600 },
];

function timeframeLabel(seconds) {
  const match = TIMEFRAME_OPTIONS.find((option) => option.seconds === Number(seconds));
  if (match) {
    return match.label;
  }
  const minutes = Number(seconds) / 60;
  return Number.isFinite(minutes) ? `${minutes}m` : String(seconds);
}

function formatDateTime(value) {
  if (!value) {
    return "--";
  }
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value)
    );
  } catch (error) {
    return "--";
  }
}

function formatNumber(value, digits = 2) {
  return Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : "--";
}

function maskKey(key) {
  if (!key) {
    return "";
  }
  if (key.length <= 12) {
    return key;
  }
  return `${key.slice(0, 7)}${"•".repeat(10)}${key.slice(-4)}`;
}

export default function AutoTraderDashboard({
  user,
  onLogout,
  onNavigateLive,
  onNavigateResults,
  onNavigateRadar,
  onNavigateAutoTrader,
}) {
  const [loading, setLoading] = useState(true);

  const [apiKey, setApiKey] = useState(null);
  const [keyRevealed, setKeyRevealed] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);
  const [keyError, setKeyError] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");

  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsJustSaved, setSettingsJustSaved] = useState(false);

  const [watches, setWatches] = useState([]);
  const [watchSymbol, setWatchSymbol] = useState("");
  const [watchTimeframe, setWatchTimeframe] = useState(300);
  const [watchSlPips, setWatchSlPips] = useState("");
  const [watchTpPips, setWatchTpPips] = useState("");
  const [watchBusy, setWatchBusy] = useState(false);
  const [watchError, setWatchError] = useState("");
  const [removingWatchId, setRemovingWatchId] = useState(null);

  const [signals, setSignals] = useState([]);
  const [events, setEvents] = useState([]);
  const [historyError, setHistoryError] = useState("");

  const loadKey = useCallback(async () => {
    try {
      const payload = await getEaApiKey();
      setApiKey(payload);
      setKeyError("");
    } catch (error) {
      setKeyError(error.message || "Failed to load API key");
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const payload = await getEaSettings();
      setSettings(payload.settings);
      setSettingsForm(payload.settings);
      setSettingsError("");
    } catch (error) {
      setSettingsError(error.message || "Failed to load settings");
    }
  }, []);

  const loadWatchlist = useCallback(async () => {
    try {
      const payload = await getWatchlist();
      setWatches(payload.watches || []);
      setWatchError("");
    } catch (error) {
      setWatchError(error.message || "Failed to load watchlist");
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const [signalsPayload, eventsPayload] = await Promise.all([getEaSignals(50), getEaEvents(50)]);
      setSignals(signalsPayload.signals || []);
      setEvents(eventsPayload.events || []);
      setHistoryError("");
    } catch (error) {
      setHistoryError(error.message || "Failed to load EA history");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      await Promise.all([loadKey(), loadSettings(), loadWatchlist(), loadHistory()]);
      if (!cancelled) {
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadKey, loadSettings, loadWatchlist, loadHistory]);

  useEffect(() => {
    const interval = setInterval(loadHistory, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadHistory]);

  const handleCopyKey = useCallback(() => {
    if (!apiKey?.apiKey || !navigator.clipboard) {
      return;
    }
    navigator.clipboard
      .writeText(apiKey.apiKey)
      .then(() => {
        setCopyFeedback("Copied!");
        setTimeout(() => setCopyFeedback(""), 2000);
      })
      .catch(() => {});
  }, [apiKey]);

  const handleRegenerateKey = useCallback(async () => {
    const confirmed = window.confirm(
      "Regenerate your EA API key? The old key stops working immediately - any running EA using it will need the new key pasted in before it can poll for signals again."
    );
    if (!confirmed) {
      return;
    }

    setKeyBusy(true);
    setKeyError("");
    try {
      const payload = await regenerateEaApiKey();
      setApiKey(payload);
      setKeyRevealed(true);
    } catch (error) {
      setKeyError(error.message || "Failed to regenerate API key");
    } finally {
      setKeyBusy(false);
    }
  }, []);

  const settingsDirty = useMemo(() => {
    if (!settings || !settingsForm) {
      return false;
    }
    return (
      Boolean(settings.autoTradingEnabled) !== Boolean(settingsForm.autoTradingEnabled) ||
      Number(settings.defaultLot) !== Number(settingsForm.defaultLot) ||
      Number(settings.defaultSlPips) !== Number(settingsForm.defaultSlPips) ||
      Number(settings.defaultTpPips) !== Number(settingsForm.defaultTpPips) ||
      Number(settings.magicNumber) !== Number(settingsForm.magicNumber) ||
      Number(settings.maxOpenTrades) !== Number(settingsForm.maxOpenTrades) ||
      Number(settings.maxDailyLoss) !== Number(settingsForm.maxDailyLoss)
    );
  }, [settings, settingsForm]);

  const handleSettingsField = (key, value) => {
    setSettingsJustSaved(false);
    setSettingsForm((form) => ({ ...form, [key]: value }));
  };

  const handleSaveSettings = useCallback(async () => {
    if (!settingsForm) {
      return;
    }

    setSettingsBusy(true);
    setSettingsError("");
    try {
      const payload = await updateEaSettings({
        autoTradingEnabled: Boolean(settingsForm.autoTradingEnabled),
        defaultLot: Number(settingsForm.defaultLot),
        defaultSlPips: Number(settingsForm.defaultSlPips),
        defaultTpPips: Number(settingsForm.defaultTpPips),
        magicNumber: Number(settingsForm.magicNumber),
        maxOpenTrades: Number(settingsForm.maxOpenTrades),
        maxDailyLoss: Number(settingsForm.maxDailyLoss),
      });
      setSettings(payload.settings);
      setSettingsForm(payload.settings);
      setSettingsJustSaved(true);
    } catch (error) {
      setSettingsError(error.message || "Failed to save settings");
    } finally {
      setSettingsBusy(false);
    }
  }, [settingsForm]);

  const handleAddWatch = useCallback(
    async (event) => {
      event.preventDefault();

      if (!watchSymbol.trim()) {
        setWatchError("Symbol is required");
        return;
      }

      setWatchBusy(true);
      setWatchError("");
      try {
        await addWatchlistEntry({
          symbol: watchSymbol.trim(),
          timeframe: Number(watchTimeframe),
          slPips: watchSlPips !== "" ? Number(watchSlPips) : undefined,
          tpPips: watchTpPips !== "" ? Number(watchTpPips) : undefined,
        });
        setWatchSymbol("");
        setWatchSlPips("");
        setWatchTpPips("");
        await loadWatchlist();
      } catch (error) {
        setWatchError(error.message || "Failed to add symbol");
      } finally {
        setWatchBusy(false);
      }
    },
    [watchSymbol, watchTimeframe, watchSlPips, watchTpPips, loadWatchlist]
  );

  const handleRemoveWatch = useCallback(
    async (watch) => {
      const confirmed = window.confirm(
        `Remove ${watch.symbol} (${timeframeLabel(watch.timeframe)}) from your auto-trade watchlist?`
      );
      if (!confirmed) {
        return;
      }

      setRemovingWatchId(watch.id);
      setWatchError("");
      try {
        await removeWatchlistEntry(watch.id);
        await loadWatchlist();
      } catch (error) {
        setWatchError(error.message || "Failed to remove symbol");
      } finally {
        setRemovingWatchId(null);
      }
    },
    [loadWatchlist]
  );

  const saveButtonDisabled = settingsBusy || !settingsDirty;

  return (
    <div className="radar-dashboard-shell">
      <div className="chart-toolbar" style={{ position: "sticky", top: 0, zIndex: 100 }}>
        <div className="dashboard-route-nav dashboard-route-nav-toolbar">
          <button type="button" className="dashboard-route-button" onClick={onNavigateLive}>
            Live Workspace
          </button>
          <button type="button" className="dashboard-route-button" onClick={onNavigateResults}>
            Saved Results
          </button>
          <button type="button" className="dashboard-route-button" onClick={onNavigateRadar}>
            Radar
          </button>
          <button
            type="button"
            className="dashboard-route-button dashboard-route-button-active"
            onClick={onNavigateAutoTrader}
          >
            Auto Trader
          </button>
        </div>

        <div className="chart-toolbar-side">
          <div className="toolbar-session">
            <div className="toolbar-session-copy">
              <span className="toolbar-session-kicker">Signed In</span>
              <strong className="toolbar-session-name">
                {user?.full_name || user?.email || "Spike User"}
              </strong>
            </div>
            <button type="button" className="toolbar-session-logout" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={styles.pageHeader}>
        <div style={styles.kicker}>MT5 Signal Execution</div>
        <h1 style={styles.title}>Auto Trader</h1>
        <p style={styles.subtitle}>
          Connect the MT5AutoTrader EA to your account, choose which symbols it's allowed to
          auto-trade, and watch every signal it executes.
        </p>
      </div>

      {loading ? (
        <div className="results-chart-empty">Loading your Auto Trader configuration...</div>
      ) : (
        <div style={styles.grid}>
          <section style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>EA API Key</h2>
              <span style={styles.cardHint}>Paste into the EA's "API Key" input in MT5</span>
            </div>
            {keyError ? <div style={styles.errorBanner}>{keyError}</div> : null}
            <div style={styles.keyRow}>
              <code style={styles.keyValue}>
                {apiKey ? (keyRevealed ? apiKey.apiKey : maskKey(apiKey.apiKey)) : "Loading..."}
              </code>
              <button
                type="button"
                style={styles.smallButton}
                onClick={() => setKeyRevealed((value) => !value)}
              >
                {keyRevealed ? "Hide" : "Show"}
              </button>
              <button type="button" style={styles.smallButton} onClick={handleCopyKey}>
                {copyFeedback || "Copy"}
              </button>
            </div>
            <div style={styles.metaRow}>
              <span>Created: {formatDateTime(apiKey?.createdAt)}</span>
              <span>Last used: {formatDateTime(apiKey?.lastUsedAt)}</span>
            </div>
            <button type="button" style={styles.dangerButton} onClick={handleRegenerateKey} disabled={keyBusy}>
              {keyBusy ? "Regenerating..." : "Regenerate Key"}
            </button>
          </section>

          <section style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Auto-Trading Settings</h2>
              <label style={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={Boolean(settingsForm?.autoTradingEnabled)}
                  onChange={(event) => handleSettingsField("autoTradingEnabled", event.target.checked)}
                />
                <span>{settingsForm?.autoTradingEnabled ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
            {settingsError ? <div style={styles.errorBanner}>{settingsError}</div> : null}
            <div style={styles.formGrid}>
              <label style={styles.field}>
                <span>Default Lot</span>
                <input
                  type="number"
                  step="0.01"
                  value={settingsForm?.defaultLot ?? ""}
                  onChange={(event) => handleSettingsField("defaultLot", event.target.value)}
                  style={styles.input}
                />
              </label>
              <label style={styles.field}>
                <span>Default SL (pips)</span>
                <input
                  type="number"
                  value={settingsForm?.defaultSlPips ?? ""}
                  onChange={(event) => handleSettingsField("defaultSlPips", event.target.value)}
                  style={styles.input}
                />
              </label>
              <label style={styles.field}>
                <span>Default TP (pips)</span>
                <input
                  type="number"
                  value={settingsForm?.defaultTpPips ?? ""}
                  onChange={(event) => handleSettingsField("defaultTpPips", event.target.value)}
                  style={styles.input}
                />
              </label>
              <label style={styles.field}>
                <span>Magic Number</span>
                <input
                  type="number"
                  value={settingsForm?.magicNumber ?? ""}
                  onChange={(event) => handleSettingsField("magicNumber", event.target.value)}
                  style={styles.input}
                />
              </label>
              <label style={styles.field}>
                <span>Max Open Trades</span>
                <input
                  type="number"
                  value={settingsForm?.maxOpenTrades ?? ""}
                  onChange={(event) => handleSettingsField("maxOpenTrades", event.target.value)}
                  style={styles.input}
                />
              </label>
              <label style={styles.field}>
                <span>Max Daily Loss (0 = off)</span>
                <input
                  type="number"
                  value={settingsForm?.maxDailyLoss ?? ""}
                  onChange={(event) => handleSettingsField("maxDailyLoss", event.target.value)}
                  style={styles.input}
                />
              </label>
            </div>
            <div style={styles.hintText}>
              This is your account-wide fallback — 100000/60000 (SL/TP) and 0.20 lot are safe
              starting values verified against a live Deriv demo account. An admin can configure a
              points-per-pip multiplier per symbol (backend "Symbol Pip Configuration") so a
              friendly pip value converts correctly for whichever symbol; unconfigured symbols
              default to 1 point per pip. Different Boom/Crash variants still need very different
              magnitudes either way — set a per-symbol override in the watchlist below rather than
              relying on one value for every symbol.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  opacity: saveButtonDisabled ? 0.5 : 1,
                  cursor: saveButtonDisabled ? "default" : "pointer",
                }}
                onClick={handleSaveSettings}
                disabled={saveButtonDisabled}
              >
                {settingsBusy ? "Saving..." : "Save Settings"}
              </button>
              {settingsJustSaved && !settingsDirty ? <span style={styles.savedTag}>Saved</span> : null}
            </div>
          </section>

          <section style={{ ...styles.card, gridColumn: "1 / -1" }}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Auto-Trade Watchlist</h2>
              <span style={styles.cardHint}>
                Only symbol/timeframe pairs listed here can trigger an automatic signal
              </span>
            </div>
            {watchError ? <div style={styles.errorBanner}>{watchError}</div> : null}
            <form style={styles.watchForm} onSubmit={handleAddWatch}>
              <input
                list="ea-symbol-options"
                placeholder="Symbol (e.g. Boom 1000 Index)"
                value={watchSymbol}
                onChange={(event) => setWatchSymbol(event.target.value)}
                style={{ ...styles.input, minWidth: 220 }}
              />
              <datalist id="ea-symbol-options">
                {RADAR_SYMBOLS.map((symbol) => (
                  <option key={symbol} value={symbol} />
                ))}
              </datalist>
              <select
                value={watchTimeframe}
                onChange={(event) => setWatchTimeframe(event.target.value)}
                style={styles.input}
              >
                {TIMEFRAME_OPTIONS.map((option) => (
                  <option key={option.seconds} value={option.seconds}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                placeholder="SL pips (optional)"
                value={watchSlPips}
                onChange={(event) => setWatchSlPips(event.target.value)}
                style={{ ...styles.input, width: 140 }}
              />
              <input
                type="number"
                placeholder="TP pips (optional)"
                value={watchTpPips}
                onChange={(event) => setWatchTpPips(event.target.value)}
                style={{ ...styles.input, width: 140 }}
              />
              <button
                type="submit"
                style={{ ...styles.primaryButton, opacity: watchBusy ? 0.6 : 1 }}
                disabled={watchBusy}
              >
                {watchBusy ? "Adding..." : "Add to Watchlist"}
              </button>
            </form>
            <div style={styles.hintText}>
              Leave SL/TP blank to use your account-wide default above. Set them per symbol here
              when different Boom/Crash variants (or any other symbol) need a different pip
              distance than that default.
            </div>
            <div className="results-table-wrap">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Timeframe</th>
                    <th>Market</th>
                    <th>SL / TP (pips)</th>
                    <th>Status</th>
                    <th>Added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {watches.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="results-table-empty">
                        No symbols on your auto-trade watchlist yet.
                      </td>
                    </tr>
                  ) : (
                    watches.map((watch) => (
                      <tr key={watch.id}>
                        <td>{watch.symbol}</td>
                        <td>{timeframeLabel(watch.timeframe)}</td>
                        <td>{watch.market}</td>
                        <td>
                          {watch.sl_pips ?? "default"} / {watch.tp_pips ?? "default"}
                        </td>
                        <td>{watch.enabled ? "Active" : "Disabled"}</td>
                        <td>{formatDateTime(watch.created_at)}</td>
                        <td>
                          <button
                            type="button"
                            style={styles.smallButton}
                            onClick={() => handleRemoveWatch(watch)}
                            disabled={removingWatchId === watch.id}
                          >
                            {removingWatchId === watch.id ? "Removing..." : "Remove"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section style={{ ...styles.card, gridColumn: "1 / -1" }}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Recent Signals</h2>
            </div>
            {historyError ? <div style={styles.errorBanner}>{historyError}</div> : null}
            <div className="results-table-wrap">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Action</th>
                    <th>Lot / SL / TP</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Ticket</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {signals.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="results-table-empty">
                        No EA signals yet.
                      </td>
                    </tr>
                  ) : (
                    signals.map((signal) => (
                      <tr key={signal.signal_uid}>
                        <td>{signal.symbol}</td>
                        <td>{signal.action}</td>
                        <td>
                          {signal.lot} / {signal.sl} / {signal.tp}
                        </td>
                        <td>{signal.source}</td>
                        <td>{signal.status}</td>
                        <td>{signal.ticket || "--"}</td>
                        <td>{formatDateTime(signal.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section style={{ ...styles.card, gridColumn: "1 / -1" }}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Recent EA Status Events</h2>
            </div>
            <div className="results-table-wrap">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Symbol / Ticket</th>
                    <th>Profit</th>
                    <th>Balance / Equity</th>
                    <th>Message</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="results-table-empty">
                        No EA status events yet.
                      </td>
                    </tr>
                  ) : (
                    events.map((eventRow, index) => (
                      <tr key={`${eventRow.event_type}-${eventRow.created_at}-${index}`}>
                        <td>{eventRow.event_type}</td>
                        <td>
                          {eventRow.symbol} {eventRow.ticket ? `#${eventRow.ticket}` : ""}
                        </td>
                        <td>{formatNumber(eventRow.profit)}</td>
                        <td>
                          {formatNumber(eventRow.balance)} / {formatNumber(eventRow.equity)}
                        </td>
                        <td>{eventRow.message || "--"}</td>
                        <td>{formatDateTime(eventRow.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

const styles = {
  pageHeader: {
    padding: "18px 4px 6px",
    display: "grid",
    gap: 4,
  },
  kicker: {
    fontSize: 10,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#48b7ff",
    fontWeight: 800,
  },
  title: { fontSize: 22, fontWeight: 800, color: "#ffffff", margin: 0 },
  subtitle: { fontSize: 12, color: "#94a3b8", maxWidth: 640, lineHeight: 1.5, margin: 0 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 14,
    paddingBottom: 32,
  },
  card: {
    borderRadius: 16,
    border: "1px solid rgba(74,115,170,0.35)",
    background: "linear-gradient(180deg, rgba(9,18,37,0.94) 0%, rgba(17,27,49,0.94) 100%)",
    color: "#e5e7eb",
    padding: "14px 16px 16px",
    display: "grid",
    gap: 10,
    boxShadow: "0 8px 24px rgba(2,6,23,0.38)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  cardTitle: { fontSize: 14, fontWeight: 800, color: "#ffffff", margin: 0 },
  cardHint: { fontSize: 10, color: "#64748b" },
  errorBanner: {
    fontSize: 11,
    color: "#fca5a5",
    background: "rgba(127,29,29,0.25)",
    border: "1px solid rgba(248,113,113,0.4)",
    borderRadius: 8,
    padding: "6px 10px",
  },
  keyRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  keyValue: {
    flex: 1,
    minWidth: 180,
    padding: "8px 10px",
    borderRadius: 8,
    background: "rgba(2,6,23,0.5)",
    border: "1px solid rgba(71,85,105,0.5)",
    color: "#93c5fd",
    fontSize: 12,
    letterSpacing: "0.02em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  smallButton: {
    padding: "7px 10px",
    borderRadius: 8,
    border: "1px solid rgba(82,105,143,0.6)",
    background: "rgba(16,26,45,0.9)",
    color: "#d8e4f8",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  metaRow: { display: "flex", gap: 14, fontSize: 10, color: "#64748b", flexWrap: "wrap" },
  dangerButton: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid rgba(248,113,113,0.5)",
    background: "rgba(127,29,29,0.25)",
    color: "#fca5a5",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    justifySelf: "start",
  },
  toggleRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#e2e8f0", cursor: "pointer" },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: 10,
  },
  field: {
    display: "grid",
    gap: 4,
    fontSize: 10,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  input: {
    padding: "7px 9px",
    borderRadius: 8,
    border: "1px solid rgba(71,85,105,0.5)",
    background: "rgba(2,6,23,0.5)",
    color: "#e5e7eb",
    fontSize: 12,
  },
  hintText: { fontSize: 10, color: "#64748b", lineHeight: 1.5 },
  primaryButton: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid rgba(56,189,248,0.5)",
    background: "rgba(56,189,248,0.15)",
    color: "#7dd3fc",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    justifySelf: "start",
  },
  savedTag: { fontSize: 10, color: "#4ade80", fontWeight: 700 },
  watchForm: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
};
